import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    facility: '',
    service: '',
    message: ''
  });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    }
    
    if (!formData.facility.trim()) {
      newErrors.facility = 'Nome da instalação é obrigatório';
    }
    
    if (!formData.service) {
      newErrors.service = 'Selecione um assunto';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Mensagem é obrigatória';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 201) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          facility: '',
          service: '',
          message: ''
        });
        setErrors({});
        // Google Ads Conversion Tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18096222373/RP-mCKjrrZ0cEKXh-bRD',
            'value': 1.0,
            'currency': 'BRL'
          });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Contato
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entre em contato com nossa equipe para vendas de equipamentos, locação, manutenção, orçamento e consultoria
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Entre em Contato
                </h2>
                <p className="text-gray-600">
                  Tem dúvidas ou precisa de um orçamento? Nossa equipe está pronta para ajudá-lo a encontrar a solução perfeita para sua necessidade.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Phone className="text-blue-600" size={24} />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Telefone</h3>
                      <p className="text-gray-600">+55 31 3482-8808</p>
                      <p className="text-gray-600">+55 31 98454-3225</p>
                      <p className="text-gray-600">+55 31 98454-2800</p>
                      <p className="text-sm text-gray-500 mt-1">Seg-Sex 8h-18h</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Mail className="text-blue-600" size={24} />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">E-mail</h3>
                      <p className="text-gray-600">visiomed@visiomed.com.br</p>
                      <p className="text-sm text-gray-500 mt-1">Resposta em horário comercial</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <MapPin className="text-blue-600" size={24} />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Localização</h3>
                      <p className="text-gray-600">Rua João Gualberto Filho 361</p>
                      <p className="text-gray-600">Bairro Sagrada Família</p>
                      <p className="text-gray-600">Belo Horizonte/MG</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Contato</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Success Message */}
                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                      <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                      <div>
                        <p className="font-semibold text-green-800">Mensagem enviada com sucesso!</p>
                        <p className="text-green-700 text-sm">Entraremos em contato em até 24 horas.</p>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                      <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                      <div>
                        <p className="font-semibold text-red-800">Erro ao enviar mensagem</p>
                        <p className="text-red-700 text-sm">Por favor, tente novamente ou entre em contato por telefone.</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome Completo *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Dr. João Silva"
                          value={formData.name}
                          onChange={handleChange}
                          className={errors.name ? 'border-red-500' : ''}
                        />
                        {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">E-mail *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="joao@hospital.com.br"
                          value={formData.email}
                          onChange={handleChange}
                          className={errors.email ? 'border-red-500' : ''}
                        />
                        {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+55 (11) 98765-4321"
                          value={formData.phone}
                          onChange={handleChange}
                          className={errors.phone ? 'border-red-500' : ''}
                        />
                        {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="facility">Nome da Instalação *</Label>
                        <Input
                          id="facility"
                          name="facility"
                          placeholder="Hospital São Paulo"
                          value={formData.facility}
                          onChange={handleChange}
                          className={errors.facility ? 'border-red-500' : ''}
                        />
                        {errors.facility && <p className="text-red-500 text-sm">{errors.facility}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service">Assunto *</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => {
                          setFormData({...formData, service: value});
                          if (errors.service) {
                            setErrors({...errors, service: null});
                          }
                        }}
                      >
                        <SelectTrigger className={errors.service ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Selecione um assunto" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equipment-sales">Vendas de Equipamentos</SelectItem>
                          <SelectItem value="rental">Locação</SelectItem>
                          <SelectItem value="maintenance">Serviço de Manutenção</SelectItem>
                          <SelectItem value="budget">Orçamento</SelectItem>
                          <SelectItem value="consultation">Consultoria</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.service && <p className="text-red-500 text-sm">{errors.service}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Mensagem *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Conte-nos sobre suas necessidades..."
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        className={errors.message ? 'border-red-500' : ''}
                      />
                      {errors.message && <p className="text-red-500 text-sm">{errors.message}</p>}
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};